import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableHead,
  TableRow,
  Typography,
  Divider
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons';

const useStyles = makeStyles(theme => ({
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end'
  },
  margin: {
    margin: `${theme.spacing(1)}px 0`
  },
  archived: {
    backgroundColor: '#f1f1f1'
  },
  unarchived: {},
  header: {
    cursor: 'pointer',
    justifyContent: 'space-between',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      '& $showSort': {
        color: 'black'
      }
    }
  },
  showSort: {
    color: 'transparent'
  }
}));

function Results({
  results,
  handleChangePage,
  handleChangeRowsPerPage,
  headers,
  cells,
  className,
  noPagination,
  autoPaginate,
  rowsPerPageValue=5,
  ...rest
}) {
  const classes = useStyles();

  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageValue);
  const [page, setPage] = React.useState(0);

  if (!results)
    return (
      <Card {...rest} className={className}>
        <CardContent>
          <Typography variant="h4">No results</Typography>
          <Divider className={classes.margin} />
          <Typography variant="subtitle1">
            There is nothing to show. Try creating some data.
          </Typography>
        </CardContent>
      </Card>
    );

  let data = results;
  if (results && results.edges) {
    data = results.edges;
  }

  const totalResults = data.length;

  if (autoPaginate) {
    data = data.slice(
      page * rowsPerPage,
      Math.floor(page * rowsPerPage + rowsPerPage, data.length - 1)
    );
  }

  return (
    <Card {...rest} className={className}>
      <Table>
        <TableHead>
          <TableRow>
            {headers &&
              headers.map((header, i) => {
                if (typeof header === 'string') {
                  return <TableCell key={i}>{header}</TableCell>;
                } else {
                  return (
                    <TableCell key={i} onClick={header.onClick}>
                      <div className={classes.header}>
                        {header.label}
                        {header?.sort?.ascending && (
                          <FontAwesomeIcon icon={faSortDown} />
                        )}
                        {header?.sort?.ascending === false && (
                          <FontAwesomeIcon icon={faSortUp} />
                        )}
                        {!header.sort && (
                          <div className={classes.showSort}>
                            <FontAwesomeIcon icon={faSort} />
                          </div>
                        )}
                      </div>
                    </TableCell>
                  );
                }
              })}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.map((result, rowIndex) => (
              <TableRow
                className={
                  result.archived ? classes.archived : classes.unarchived
                }
                key={result.uuid || result.key}
              >
                {cells &&
                  cells.map((cell, i) => (
                    <TableCell key={i}>
                      {cell.component
                        ? cell.component(result, rowIndex)
                        : result[cell.field]}
                    </TableCell>
                  ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {!noPagination && !autoPaginate && (
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={results?.pageInfo?.total ?? 0}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            page={
              results?.pageInfo
                ? Math.floor(results.pageInfo.offset / results.pageInfo.limit)
                : 0
            }
            rowsPerPage={rowsPerPageValue}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardActions>
      )}
      {autoPaginate && (
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={totalResults}
            onChangePage={(evt, page) => {
              setPage(page);
            }}
            onChangeRowsPerPage={evt => setRowsPerPage(evt.target.value)}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardActions>
      )}
    </Card>
  );
}

Results.propTypes = {
  results: PropTypes.oneOfType([
    PropTypes.exact({
      edges: PropTypes.array,
      pageInfo: PropTypes.exact({
        total: PropTypes.number,
        offset: PropTypes.number,
        limit: PropTypes.number,
        given: PropTypes.number,
        __typename: PropTypes.string
      }),
      __typename: PropTypes.string
    }),
    PropTypes.array
  ]),
  className: PropTypes.string,
  headers: PropTypes.array,
  cells: PropTypes.array,
  handleChangePage: PropTypes.func,
  handleChangeRowsPerPage: PropTypes.func
};

export default Results;
